import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Building2, 
  Users, 
  FileText, 
  Calendar, 
  DollarSign, 
  Plus, 
  ChevronLeft,
  ListPlus,
  User,
  Target,
  BookText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { DepartmentDetail as DepartmentDetailType } from "@/types/department";
import { MFOItem } from "@/types/kanban";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { updateDepartment } from "@/utils/supabase/departmentUtils";

const DepartmentDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [department, setDepartment] = useState<DepartmentDetailType | null>(null);
  const [mfos, setMfos] = useState<MFOItem[]>([]);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editedDepartment, setEditedDepartment] = useState({
    name: "",
    description: "",
    headName: "",
    headTitle: "",
    budget: 0,
    vision: "",
    mission: "",
    goals: [""]
  });

  useEffect(() => {
    const fetchDepartmentDetails = async () => {
      try {
        const { data, error } = await supabase
          .from('departments')
          .select(`
            *,
            department_goals (goal)
          `)
          .eq('id', id)
          .single();

        if (error) throw error;

        const departmentDetail: DepartmentDetailType = {
          ...data,
          headName: data.head_name,
          headTitle: data.head_title,
          mfoCount: data.mfo_count,
          vision: data.vision || "",
          mission: data.mission || "",
          goals: data.department_goals?.map(g => g.goal) || [],
          collaborators: [], // Initialize as empty array, will be populated if needed
          year: data.year || new Date().getFullYear(),
          budget: data.budget || 0,
          description: data.description || ""
        };

        setDepartment(departmentDetail);
      } catch (error) {
        console.error("Error fetching department details:", error);
        toast({
          title: "Error",
          description: "Failed to load department details",
          variant: "destructive",
        });
      }
    };

    const fetchDepartmentMFOs = async () => {
      try {
        const { data, error } = await supabase
          .from('mfo_items')
          .select(`
            *,
            mfo_success_indicators (indicator),
            mfo_assignees (
              employee:employees (
                id,
                name,
                avatar
              )
            )
          `)
          .eq('department_id', id);

        if (error) throw error;

        const formattedMFOs: MFOItem[] = data.map(mfo => ({
          id: mfo.id,
          code: mfo.code,
          title: mfo.title,
          budget: mfo.budget || 0,
          priority: mfo.priority || "medium",
          dueDate: mfo.due_date,
          status: mfo.status || "not-started",
          progress: mfo.progress || 0,
          departmentId: mfo.department_id,
          successIndicators: mfo.mfo_success_indicators?.map(indicator => indicator.indicator) || [],
          timeline: {
            q1: mfo.q1 || false,
            q2: mfo.q2 || false,
            q3: mfo.q3 || false,
            q4: mfo.q4 || false
          },
          collaborators: mfo.mfo_assignees?.map(assignee => ({
            id: assignee.employee?.id,
            name: assignee.employee?.name,
            avatar: assignee.employee?.avatar
          })) || []
        }));

        setMfos(formattedMFOs);
      } catch (error) {
        console.error("Error fetching department MFOs:", error);
        toast({
          title: "Error",
          description: "Failed to load MFOs",
          variant: "destructive",
        });
      }
    };

    fetchDepartmentDetails();
    fetchDepartmentMFOs();
  }, [id, toast]);

  const handleEditClick = () => {
    if (!department) return;
    
    setEditedDepartment({
      name: department.name,
      description: department.description || "",
      headName: department.headName,
      headTitle: department.headTitle,
      budget: department.budget,
      vision: department.vision || "",
      mission: department.mission || "",
      goals: department.goals && department.goals.length > 0 ? department.goals : [""]
    });
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!department) return;
    
    try {
      await updateDepartment(department.id, editedDepartment);
      
      setDepartment(prev => prev ? {
        ...prev,
        name: editedDepartment.name,
        description: editedDepartment.description,
        headName: editedDepartment.headName,
        headTitle: editedDepartment.headTitle,
        budget: editedDepartment.budget,
        vision: editedDepartment.vision,
        mission: editedDepartment.mission,
        goals: editedDepartment.goals
      } : null);
      
      toast({
        title: "Success",
        description: "Department updated successfully",
      });
      setIsEditDialogOpen(false);
    } catch (error) {
      console.error("Error updating department:", error);
      toast({
        title: "Error",
        description: "Failed to update department",
        variant: "destructive",
      });
    }
  };

  if (!department || !Array.isArray(department.goals)) {
    return (
      <div className="flex flex-col items-center justify-center h-[70vh]">
        <h2 className="text-2xl font-bold mb-4">Department not found</h2>
        <Button onClick={() => navigate("/dashboard/divisions")}>
          <ChevronLeft className="mr-2 h-4 w-4" />
          Back to Divisions
        </Button>
      </div>
    );
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "in-progress":
        return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200">In Progress</Badge>;
      case "not-started":
        return <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-200">Not Started</Badge>;
      case "completed":
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-200">Completed</Badge>;
      case "delayed":
        return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-200">Delayed</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">Unknown</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2">
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => navigate("/dashboard/divisions")}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <h2 className="text-3xl font-bold tracking-tight">{department.name}</h2>
      </div>
      
      <div className="bg-card rounded-lg border shadow-sm p-6">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="md:w-1/3">
            <div className="flex flex-col items-center p-6 bg-background rounded-lg border">
              <Building2 className="h-12 w-12 text-primary mb-4" />
              <h3 className="text-xl font-bold">{department.name}</h3>
              <p className="text-muted-foreground text-sm text-center mt-2 mb-4">{department.description}</p>
              
              <div className="w-full border-t pt-4 mt-2">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">Department Head:</span>
                  <span>{department.headName}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">Position:</span>
                  <span>{department.headTitle}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">Budget (2025):</span>
                  <span>₱{department.budget.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium">Total MFOs:</span>
                  <span>{mfos?.length}</span>
                </div>
              </div>
              
            </div>
          </div>
          
          <div className="md:w-2/3">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="mfos">MFOs</TabsTrigger>
                <TabsTrigger value="collaborators">Collaborators</TabsTrigger>
              </TabsList>
              
              <TabsContent value="overview" className="pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center">
                        <Target className="h-5 w-5 mr-2 text-primary" />
                        Vision
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm">{department.vision}</p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center">
                        <BookText className="h-5 w-5 mr-2 text-primary" />
                        Mission
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm">{department.mission}</p>
                    </CardContent>
                  </Card>
                </div>
                
                <Card className="mt-4">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center">
                      <Target className="h-5 w-5 mr-2 text-primary" />
                      Strategic Goals
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="list-disc list-inside space-y-2">
                      {department?.goals?.map((goal, index) => (
                        <li key={index} className="text-sm">{goal}</li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
                
                <div className="flex justify-between mt-6">
                  <Button 
                    variant="outline"
                    onClick={() => navigate(`/dashboard/mfo?department=${department.id}`)}
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    View All MFOs
                  </Button>
                  <Button 
                    onClick={() => navigate(`/dashboard/kanban?department=${department.id}`)}
                  >
                    <ListPlus className="mr-2 h-4 w-4" />
                    View Kanban Board
                  </Button>
                </div>
              </TabsContent>
              
              <TabsContent value="mfos" className="pt-4">
                <div className="flex justify-between mb-4">
                  <h3 className="text-lg font-medium">Major Final Outputs ({mfos?.length})</h3>
                 
                </div>
                
                <div className="space-y-4">
                  {mfos?.map((mfo) => (
                    <Card key={mfo.id} className="overflow-hidden">
                      <CardHeader className="pb-2 bg-muted/50">
                        <div className="flex justify-between">
                          <CardTitle className="text-lg flex items-center">
                            <span className="text-sm font-bold bg-primary/10 px-2 py-1 rounded mr-2">
                              {mfo.code}
                            </span>
                            {mfo.title}
                          </CardTitle>
                          {getStatusBadge(mfo.status)}
                        </div>
                      </CardHeader>
                      <CardContent className="pt-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h4 className="text-sm font-medium mb-2">Success Indicators:</h4>
                            <ul className="list-disc list-inside space-y-1">
                              {mfo.successIndicators?.map((indicator, idx) => (
                                <li key={idx} className="text-sm text-muted-foreground">
                                  {indicator}
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          <div className="space-y-3">
                            <div className="flex justify-between text-sm">
                              <span className="font-medium flex items-center">
                                <DollarSign className="h-4 w-4 mr-1" />
                                Budget:
                              </span>
                              <span>₱{mfo.budget.toLocaleString()}</span>
                            </div>
                            
                            <div className="flex justify-between text-sm">
                              <span className="font-medium flex items-center">
                                <Calendar className="h-4 w-4 mr-1" />
                                Timeline:
                              </span>
                              <div className="flex space-x-2">
                                <Badge variant={mfo.timeline?.q1 ? "default" : "outline"} className="rounded-sm">Q1</Badge>
                                <Badge variant={mfo.timeline?.q2 ? "default" : "outline"} className="rounded-sm">Q2</Badge>
                                <Badge variant={mfo.timeline?.q3 ? "default" : "outline"} className="rounded-sm">Q3</Badge>
                                <Badge variant={mfo.timeline?.q4 ? "default" : "outline"} className="rounded-sm">Q4</Badge>
                              </div>
                            </div>
                            
                            <div className="flex justify-between text-sm">
                              <span className="font-medium flex items-center">
                                <Users className="h-4 w-4 mr-1" />
                                Collaborators:
                              </span>
                              <div className="flex -space-x-2">
                                {mfo.collaborators?.slice(0, 3)?.map((collaborator, idx) => (
                                  <Avatar key={idx} className="h-6 w-6 border-2 border-background">
                                    <AvatarFallback className="text-[10px] bg-primary text-primary-foreground">
                                      {collaborator.name.split(" ").map(n => n[0]).join("")}
                                    </AvatarFallback>
                                  </Avatar>
                                ))}
                                {mfo.collaborators?.length > 3 && (
                                  <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center text-[10px] border-2 border-background">
                                    +{mfo.collaborators?.length - 3}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex justify-end space-x-2 mt-4">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => navigate(`/dashboard/mfo/${mfo.id}/edit`)}
                          >
                            Edit
                          </Button>
                          <Button 
                            size="sm" 
                            variant="default"
                            onClick={() => navigate(`/dashboard/mfo/${mfo.id}`)}
                          >
                            View Details
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
              
              <TabsContent value="collaborators" className="pt-4">
                <div className="flex justify-between mb-4">
                  <h3 className="text-lg font-medium">Department Collaborators ({department.collaborators?.length})</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {department.collaborators.map((collaborator) => (
                    <Card key={collaborator.id} className="overflow-hidden">
                      <CardContent className="pt-6 flex items-center">
                        <Avatar className="h-12 w-12 mr-4">
                          <AvatarFallback className="bg-primary text-primary-foreground">
                            {collaborator.name.split(" ").map(n => n[0]).join("")}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <h4 className="font-medium">{collaborator.name}</h4>
                          <p className="text-sm text-muted-foreground">Collaborator</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Department</DialogTitle>
            <DialogDescription>
              Update the information for {department?.name}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-name">Department Name*</Label>
              <Input
                id="edit-name"
                value={editedDepartment.name}
                onChange={(e) => setEditedDepartment({ ...editedDepartment, name: e.target.value })}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={editedDepartment.description}
                onChange={(e) => setEditedDepartment({ ...editedDepartment, description: e.target.value })}
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-headName">Department Head Name*</Label>
                <Input
                  id="edit-headName"
                  value={editedDepartment.headName}
                  onChange={(e) => setEditedDepartment({ ...editedDepartment, headName: e.target.value })}
                />
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="edit-headTitle">Head Title*</Label>
                <Input
                  id="edit-headTitle"
                  value={editedDepartment.headTitle}
                  onChange={(e) => setEditedDepartment({ ...editedDepartment, headTitle: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="edit-budget">Budget (₱)*</Label>
              <Input
                id="edit-budget"
                type="number"
                value={editedDepartment.budget || ""}
                onChange={(e) => setEditedDepartment({ ...editedDepartment, budget: parseFloat(e.target.value) || 0 })}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="edit-vision">Department Vision</Label>
              <Textarea
                id="edit-vision"
                placeholder="Enter department vision statement"
                value={editedDepartment.vision}
                onChange={(e) => setEditedDepartment({ ...editedDepartment, vision: e.target.value })}
                rows={3}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="edit-mission">Department Mission</Label>
              <Textarea
                id="edit-mission"
                placeholder="Enter department mission statement"
                value={editedDepartment.mission}
                onChange={(e) => setEditedDepartment({ ...editedDepartment, mission: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DepartmentDetail;
